import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.polarisos.uk';

axios.defaults.baseURL = API_BASE_URL;

const REFRESH_MARGIN_SECONDS = 180;
let refreshTimeoutId = null;
let refreshInFlight = null;

axios.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      const hasAuthHeader =
        config.headers?.Authorization || config.headers?.authorization;
      if (!hasAuthHeader) {
        const token = window.localStorage.getItem('authToken');
        if (token) {
          if (!config.headers) {
            config.headers = {};
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function getStoredExpiresAt() {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = window.localStorage.getItem('tokenExpiresAt');
  if (!raw) {
    return null;
  }
  const numeric = Number(raw);
  if (!Number.isNaN(numeric) && numeric > 0) {
    return numeric;
  }
  const parsed = Date.parse(raw);
  if (!Number.isNaN(parsed)) {
    return Math.floor(parsed / 1000);
  }
  return null;
}

async function performRefresh() {
  if (refreshInFlight || typeof window === 'undefined') {
    return refreshInFlight;
  }

  const refreshToken = window.localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  const accessToken = window.localStorage.getItem('authToken');
  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

  refreshInFlight = axios
    .post(
      '/api/auth/refresh',
      { refresh_token: refreshToken },
      { headers }
    )
    .then((response) => {
      persistSession(response.data);
      return response;
    })
    .catch((err) => {
      console.error('Failed to refresh auth session', err);
      scheduleRefresh(30);
      throw err;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function scheduleRefresh(minDelaySeconds = 0) {
  if (typeof window === 'undefined') {
    return;
  }

  if (refreshTimeoutId) {
    window.clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }

  const expiresAt = getStoredExpiresAt();
  const refreshToken = window.localStorage.getItem('refreshToken');
  if (!refreshToken || expiresAt === null) {
    return;
  }

  const now = Math.floor(Date.now() / 1000);
  let delaySeconds = expiresAt - REFRESH_MARGIN_SECONDS - now;
  if (Number.isNaN(delaySeconds)) {
    delaySeconds = 0;
  }
  if (delaySeconds < minDelaySeconds) {
    delaySeconds = minDelaySeconds;
  }

  if (delaySeconds <= 0) {
    performRefresh();
    return;
  }

  refreshTimeoutId = window.setTimeout(() => {
    performRefresh();
  }, delaySeconds * 1000);
}

export function persistSession(session = {}) {
  if (typeof window === 'undefined' || !session) {
    return;
  }

  const { access_token, refresh_token, expires_in } = session;
  let { expires_at } = session;

  if (typeof access_token === 'string') {
    window.localStorage.setItem('authToken', access_token);
  } else if ('access_token' in session) {
    window.localStorage.removeItem('authToken');
  }

  if (typeof refresh_token === 'string') {
    window.localStorage.setItem('refreshToken', refresh_token);
  } else if ('refresh_token' in session) {
    window.localStorage.removeItem('refreshToken');
  }

  if ((expires_at === undefined || expires_at === null) && expires_in != null) {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const expiresInNumber = Number(expires_in);
    if (!Number.isNaN(expiresInNumber) && expiresInNumber > 0) {
      expires_at = nowSeconds + expiresInNumber;
    }
  }

  if (expires_at !== undefined && expires_at !== null) {
    window.localStorage.setItem('tokenExpiresAt', String(expires_at));
  } else if ('expires_at' in session || 'expires_in' in session) {
    window.localStorage.removeItem('tokenExpiresAt');
  }

  if (expires_in !== undefined && expires_in !== null) {
    window.localStorage.setItem('tokenExpiresIn', String(expires_in));
  } else if ('expires_in' in session) {
    window.localStorage.removeItem('tokenExpiresIn');
  }

  scheduleRefresh();
}

export function clearSession() {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem('authToken');
  window.localStorage.removeItem('refreshToken');
  window.localStorage.removeItem('tokenExpiresAt');
  window.localStorage.removeItem('tokenExpiresIn');
  if (refreshTimeoutId) {
    window.clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
  refreshInFlight = null;
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      if (typeof window !== 'undefined') {
        clearSession();
        if (window.location?.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
    }
    return Promise.reject(error);
  }
);

if (typeof window !== 'undefined') {
  scheduleRefresh();
}

export default API_BASE_URL;
